import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import CloseIcon from '../../../common/icons/CloseIcon';
import SelectField from '../../../common/SelectField';
import TextField from '../../../common/TextField';
import api from '../../../../utils/api';
import { AsyncAutocomplete, Option } from '../../../common/AsyncAutocomplete';
import { QualificationEntry, SkillEntry, TechnologyEntry } from './VacancyForm';


interface VacancySkillCardProps {
  type: 'add' | 'edit' | 'view';
  qualifications: QualificationEntry[];
  primarySkills: SkillEntry[];
  secondarySkills: SkillEntry[];
  primaryTechnologies: TechnologyEntry[];
  secondaryTechnologies: TechnologyEntry[];
  setQualifications: (qualifications: QualificationEntry[]) => void;
  setPrimarySkills: (primarySkills: SkillEntry[]) => void;
  setSecondarySkills: (secondarySkills: SkillEntry[]) => void;
  setPrimaryTechnologies: (primaryTechnologies: TechnologyEntry[]) => void;
  setSecondaryTechnologies: (secondaryTechnologies: TechnologyEntry[]) => void;
}
const VacancySkillCard: React.FC<VacancySkillCardProps> = ({ type, qualifications, primarySkills, secondarySkills, primaryTechnologies, secondaryTechnologies, setQualifications, setPrimarySkills, setSecondarySkills, setPrimaryTechnologies, setSecondaryTechnologies }) => {
  const addEntry = (
    section: 'qualifications' | 'primarySkills' | 'secondarySkills' | 'primaryTechnologies' | 'secondaryTechnologies'
  ) => {
    const newId = Date.now().toString();

    switch (section) {
      case 'qualifications':
        setQualifications([...qualifications, { id: newId, qualification: null, type: '' }]);
        break;
      case 'primarySkills':
        if (primarySkills.length < 2) {
          setPrimarySkills([...primarySkills, { id: newId, skill: null, years: '' }]);
        }
        break;
      case 'secondarySkills':
        setSecondarySkills([...secondarySkills, { id: newId, skill: null, years: '' }]);
        break;
      case 'primaryTechnologies':
        if (primaryTechnologies.length < 2) {
          setPrimaryTechnologies([...primaryTechnologies, { id: newId, technology: null, years: '' }]);
        }
        break;
      case 'secondaryTechnologies':
        setSecondaryTechnologies([...secondaryTechnologies, { id: newId, technology: null, years: '' }]);
        break;
    }
  };

  const removeEntry = (
    section: 'qualifications' | 'primarySkills' | 'secondarySkills' | 'primaryTechnologies' | 'secondaryTechnologies',
    id: string
  ) => {
    switch (section) {
      case 'qualifications':
        setQualifications(qualifications.filter(item => item.id !== id));
        break;
      case 'primarySkills':
        setPrimarySkills(primarySkills.filter(item => item.id !== id));
        break;
      case 'secondarySkills':
        setSecondarySkills(secondarySkills.filter(item => item.id !== id));
        break;
      case 'primaryTechnologies':
        setPrimaryTechnologies(primaryTechnologies.filter(item => item.id !== id));
        break;
      case 'secondaryTechnologies':
        setSecondaryTechnologies(secondaryTechnologies.filter(item => item.id !== id));
        break;
    }
  };

  const handleLoadQualifications = async (query: string, otherQuery?: string) => {
    const response = await api.get<{ data: { id: number; qualification: string }[] }>(
      `/rec/vacancy/qualifications?query=${query}&type=${otherQuery || ''}`,
      {
        headers: {
          'x-api-id': 7,
          'x-page-id': 7,
        },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadSkills = async (query: string) => {
    const response = await api.get<{ data: { id: number; qualification: string }[] }>(
      `/rec/vacancy/skills?query=${query}`,
      {
        headers: {
          'x-api-id': 8,
          'x-page-id': 7,
        },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadTechnologies = async (query: string) => {
    const response = await api.get<{ data: { id: number; qualification: string }[] }>(
      `/rec/vacancy/technologies?query=${query}`,
      {
        headers: {
          'x-api-id': 9,
          'x-page-id': 7,
        },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleQualificationChange = (id: string, field: keyof QualificationEntry, value: string | Option | null) => {
    setQualifications(qualifications.map(qual => {
      if (qual.id === id) {
        return {
          ...qual,
          [field]: value
        };
      }
      return qual;
    }));
  };

  const handleSkillChange = (id: string, field: keyof SkillEntry, value: string | Option | null) => {
    setPrimarySkills(primarySkills.map(skill => {
      if (skill.id === id) {
        return {
          ...skill,
          [field]: value
        };
      }
      return skill;
    }));
  };

  const handleTechnologyChange = (id: string, field: keyof TechnologyEntry, value: string | Option | null) => {
    setPrimaryTechnologies(primaryTechnologies.map(tech => {
      if (tech.id === id) {
        return {
          ...tech,
          [field]: value
        };
      }
      return tech;
    }));
  };

  return (
    <Box className="edit-section">
      {/* Qualifications */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: '900px' }}>
        <Typography className="edit-section-title">
          Qualifications
        </Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry('qualifications')}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {qualifications.map((qual, index) => (
        <Box key={qual.id} className="edit-sub-section" sx={{ width: '900px', mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <SelectField
                label="Type"
                width="360px"
                options={[
                  { value: 'Degree', label: 'Degree' },
                  { value: 'Diploma', label: 'Diploma' },
                  { value: 'Certificate', label: 'Certificate' },
                ]}
                value={qual.type}
                onChange={(e) => handleQualificationChange(qual.id, 'type', e.target.value as string)}
                name={`qualifications[${index}].type`}
              />
            </Box>
            <Box>
              <AsyncAutocomplete
                label="Qualification"
                width="360px"
                loadOptions={(query, otherQuery) => handleLoadQualifications(query, otherQuery)}
                otherQuery={qual.type}
                value={qual.qualification}
                onChange={(value) => handleQualificationChange(qual.id, 'qualification', value)}
              />
            </Box>
            {type !== 'view' && qualifications.length > 1 && (
              <IconButton
                onClick={() => removeEntry('qualifications', qual.id)}
                className="edit-section-close-button"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {/* Primary Skills */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: '900px' }}>
        <Typography className="edit-section-title">
          Primary Skills (2 Skills Maximum)
        </Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry('primarySkills')}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {primarySkills.map((skill, index) => (
        <Box key={skill.id} className="edit-sub-section" sx={{ width: '900px', mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Skill"
                width="360px"
                loadOptions={handleLoadSkills}
                value={skill.skill}
                onChange={(value) => handleSkillChange(skill.id, 'skill', value)}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={skill.years}
                onChange={(e) => handleSkillChange(skill.id, 'years', e.target.value as string)}
                name={`primarySkills[${index}].years`}
              />
            </Box>
            {type !== 'view' && primarySkills.length > 1 && (
              <IconButton
                onClick={() => removeEntry('primarySkills', skill.id)}
                className="edit-section-close-button"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {/* Secondary Skills */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: '900px' }}>
        <Typography className="edit-section-title">
          Secondary Skills
        </Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry('secondarySkills')}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {secondarySkills.map((skill, index) => (
        <Box key={skill.id} className="edit-sub-section" sx={{ width: '900px', mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Skill"
                width="360px"
                loadOptions={handleLoadSkills}
                value={skill.skill}
                onChange={(value) => handleSkillChange(skill.id, 'skill', value)}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={skill.years}
                onChange={(e) => handleSkillChange(skill.id, 'years', e.target.value)}
                name={`secondarySkills[${index}].years`}
              />
            </Box>
            {type !== 'view' && secondarySkills.length > 1 && (
              <IconButton
                onClick={() => removeEntry('secondarySkills', skill.id)}
                className="edit-section-close-button"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {/* Primary Technologies */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: '900px' }}>
        <Typography className="edit-section-title">
          Primary Technologies (2 Technologies Maximum)
        </Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry('primaryTechnologies')}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {primaryTechnologies.map((tech, index) => (
        <Box key={tech.id} className="edit-sub-section" sx={{ width: '900px', mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Technology"
                width="360px"
                loadOptions={handleLoadTechnologies}
                value={tech.technology}
                onChange={(value) => handleTechnologyChange(tech.id, 'technology', value)}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={tech.years}
                onChange={(e) => handleTechnologyChange(tech.id, 'years', e.target.value)}
                name={`primaryTechnologies[${index}].years`}
              />
            </Box>
            {type !== 'view' && primaryTechnologies.length > 1 && (
              <IconButton
                onClick={() => removeEntry('primaryTechnologies', tech.id)}
                className="edit-section-close-button"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {/* Secondary Technologies */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: '900px' }}>
        <Typography className="edit-section-title">
          Secondary Technologies
        </Typography>
        {type !== 'view' && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => addEntry('secondaryTechnologies')}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {secondaryTechnologies.map((tech, index) => (
        <Box key={tech.id} className="edit-sub-section" sx={{ width: '900px', mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                label="Technology"
                width="360px"
                loadOptions={handleLoadTechnologies}
                value={tech.technology}
                onChange={(value) => handleTechnologyChange(tech.id, 'technology', value)}
              />
            </Box>
            <Box>
              <TextField
                label="Years"
                width="360px"
                type="number"
                value={tech.years}
                onChange={(e) => handleTechnologyChange(tech.id, 'years', e.target.value)}
                name={`secondaryTechnologies[${index}].years`}
              />
            </Box>
            {type !== 'view' && secondaryTechnologies.length > 1 && (
              <IconButton
                onClick={() => removeEntry('secondaryTechnologies', tech.id)}
                className="edit-section-close-button"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default VacancySkillCard; 